import React from 'react';

interface InBooks24LogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'horizontal' | 'stacked' | 'icon-only';
  showTagline?: boolean;
  showIcon?: boolean;
}

export function InBooks24Logo({ 
  size = 'md', 
  variant = 'horizontal', 
  showTagline = false,
  showIcon = false
}: InBooks24LogoProps) {
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  if (variant === 'icon-only') {
    // For icon-only, show the logo icon
    const iconSizes = {
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12',
      xl: 'w-16 h-16'
    };
    
    return (
      <div className={iconSizes[size]}>
        <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
          <rect width="32" height="32" rx="6" fill="url(#blueGradient)"/>
          <path d="M8 10C8 9.45 8.45 9 9 9H14C14.55 9 15 9.45 15 10V22C15 22.55 14.55 23 14 23H9C8.45 23 8 22.55 8 22V10Z" 
                fill="none" 
                stroke="white" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"/>
          <path d="M17 10C17 9.45 17.45 9 18 9H23C23.55 9 24 9.45 24 10V22C24 22.55 23.55 23 23 23H18C17.45 23 17 22.55 17 22V10Z" 
                fill="none" 
                stroke="white" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"/>
          <path d="M15 9V23" 
                stroke="white" 
                strokeWidth="1.5" 
                strokeLinecap="round"/>
          <path d="M10 12H13M10 15H13M10 18H13" 
                stroke="white" 
                strokeWidth="1" 
                strokeLinecap="round" 
                opacity="0.8"/>
          <path d="M19 12H22M19 15H22M19 18H22" 
                stroke="white" 
                strokeWidth="1" 
                strokeLinecap="round" 
                opacity="0.8"/>
          <defs>
            <linearGradient id="blueGradient" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#0ea5e9"/>
              <stop offset="100%" stopColor="#0284c7"/>
            </linearGradient>
          </defs>
        </svg>
      </div>
    );
  }

  if (variant === 'stacked') {
    const iconSizes = {
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12',
      xl: 'w-16 h-16'
    };
    
    return (
      <div className="flex flex-col items-center space-y-2">
        {showIcon && (
          <div className={iconSizes[size]}>
            <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
              <rect width="32" height="32" rx="6" fill="url(#blueGradient)"/>
              <path d="M8 10C8 9.45 8.45 9 9 9H14C14.55 9 15 9.45 15 10V22C15 22.55 14.55 23 14 23H9C8.45 23 8 22.55 8 22V10Z" 
                    fill="none" 
                    stroke="white" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"/>
              <path d="M17 10C17 9.45 17.45 9 18 9H23C23.55 9 24 9.45 24 10V22C24 22.55 23.55 23 23 23H18C17.45 23 17 22.55 17 22V10Z" 
                    fill="none" 
                    stroke="white" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"/>
              <path d="M15 9V23" 
                    stroke="white" 
                    strokeWidth="1.5" 
                    strokeLinecap="round"/>
              <path d="M10 12H13M10 15H13M10 18H13" 
                    stroke="white" 
                    strokeWidth="1" 
                    strokeLinecap="round" 
                    opacity="0.8"/>
              <path d="M19 12H22M19 15H22M19 18H22" 
                    stroke="white" 
                    strokeWidth="1" 
                    strokeLinecap="round" 
                    opacity="0.8"/>
              <defs>
                <linearGradient id="blueGradient" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#0ea5e9"/>
                  <stop offset="100%" stopColor="#0284c7"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
        )}
        <div className="text-center">
          <div className={`${sizeClasses[size]} font-semibold tracking-tight bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent leading-tight`} style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
            InBooks24
          </div>
          {showTagline && (
            <div className={`${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-sm'} text-gray-600 mt-0.5 font-normal`} style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
              Management On Tips
            </div>
          )}
        </div>
      </div>
    );
  }

  const iconSizes = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
    xl: 'w-12 h-12'
  };

  return (
    <div className="flex items-center gap-3">
      {showIcon && (
        <div className={iconSizes[size] + ' flex-shrink-0'}>
          <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
            <rect width="32" height="32" rx="6" fill="url(#blueGradient)"/>
            <path d="M8 10C8 9.45 8.45 9 9 9H14C14.55 9 15 9.45 15 10V22C15 22.55 14.55 23 14 23H9C8.45 23 8 22.55 8 22V10Z" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"/>
            <path d="M17 10C17 9.45 17.45 9 18 9H23C23.55 9 24 9.45 24 10V22C24 22.55 23.55 23 23 23H18C17.45 23 17 22.55 17 22V10Z" 
                  fill="none" 
                  stroke="white" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"/>
            <path d="M15 9V23" 
                  stroke="white" 
                  strokeWidth="1.5" 
                  strokeLinecap="round"/>
            <path d="M10 12H13M10 15H13M10 18H13" 
                  stroke="white" 
                  strokeWidth="1" 
                  strokeLinecap="round" 
                  opacity="0.8"/>
            <path d="M19 12H22M19 15H22M19 18H22" 
                  stroke="white" 
                  strokeWidth="1" 
                  strokeLinecap="round" 
                  opacity="0.8"/>
          <defs>
            <linearGradient id="blueGradient" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#0ea5e9"/>
              <stop offset="100%" stopColor="#0284c7"/>
            </linearGradient>
          </defs>
          </svg>
        </div>
      )}
      <div>
        <div className={`${sizeClasses[size]} font-semibold tracking-tight bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent leading-tight`} style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
          InBooks24
        </div>
        {showTagline && (
          <div className={`${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-xs' : size === 'lg' ? 'text-sm' : 'text-sm'} text-gray-600 leading-tight mt-0.5 font-normal`} style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
            Management On Tips
          </div>
        )}
      </div>
    </div>
  );
}